import { EventHandler, Vec3 as PcVec3 } from 'playcanvas';
import type { ViewerAvatarFallEvent, ViewerNavigationMode } from '@mindcloudxai/shared';
import type { Global } from '../../submodules/supersplat-viewer/src/types';
import type {
  SuperSplatRuntime,
  WalkControllerRuntime,
  WalkControllerStateSnapshot,
} from './types';
import {
  AVATAR_FALL_DROP_THRESHOLD,
  AVATAR_FALL_MIN_AIRBORNE_SECONDS,
  AVATAR_FALL_MIN_VELOCITY_Y,
  AVATAR_FALL_RESET_COOLDOWN_SECONDS,
} from './constants';

export interface AvatarFallContext {
  global: Global;
  getViewer: () => SuperSplatRuntime | undefined;
  events: EventHandler;
  resetNavigation: () => void;
}

export interface AvatarFallDetector {
  detect: (deltaTime?: number) => void;
  reset: () => void;
}

export function createAvatarFallDetector(ctx: AvatarFallContext): AvatarFallDetector {
  const avatarWalkState: WalkControllerStateSnapshot = {
    eyePosition: new PcVec3(),
    footPosition: new PcVec3(),
    velocity: new PcVec3(),
    grounded: false,
    jumping: false,
    jumpStarted: false,
    jumpHadInput: false,
    yaw: 0,
    moveMagnitude: 0,
  };
  let avatarFallLastSafeY: number | null = null;
  let avatarFallAirborneSeconds = 0;
  let avatarFallResetCooldownSeconds = 0;

  function reset(): void {
    avatarFallLastSafeY = null;
    avatarFallAirborneSeconds = 0;
  }

  function getActiveWalkController(
    mode: Extract<ViewerNavigationMode, 'walk' | 'avatar'>,
  ): WalkControllerRuntime | undefined {
    const cameraManager = ctx.getViewer()?.cameraManager;
    return mode === 'avatar' ? cameraManager?.avatarController : cameraManager?.walkController;
  }

  function detect(deltaTime = 0): void {
    if (!ctx.global) return;

    avatarFallResetCooldownSeconds = Math.max(
      0,
      avatarFallResetCooldownSeconds - Math.max(deltaTime, 0),
    );

    const mode = ctx.global.state.cameraMode;
    if (mode !== 'walk' && mode !== 'avatar') {
      reset();
      return;
    }

    const controller = getActiveWalkController(mode);
    if (!controller) {
      reset();
      return;
    }

    const state = controller.getAvatarState?.(avatarWalkState);
    const position = state?.eyePosition ?? controller.navPosition;
    if (!position) return;

    if (state?.grounded) {
      avatarFallLastSafeY = position.y;
      avatarFallAirborneSeconds = 0;
      return;
    }

    const lastSafeY = avatarFallLastSafeY ?? position.y;
    avatarFallLastSafeY = lastSafeY;
    avatarFallAirborneSeconds += Math.max(deltaTime, 0);

    const dropDistance = lastSafeY - position.y;
    const velocityY = state?.velocity.y ?? 0;
    const fallen =
      avatarFallResetCooldownSeconds <= 0 &&
      avatarFallAirborneSeconds >= AVATAR_FALL_MIN_AIRBORNE_SECONDS &&
      dropDistance >= AVATAR_FALL_DROP_THRESHOLD &&
      velocityY <= AVATAR_FALL_MIN_VELOCITY_Y;

    if (!fallen) return;

    const event: ViewerAvatarFallEvent = {
      mode,
      position: { x: position.x, y: position.y, z: position.z },
      lastSafeY,
      dropDistance,
    };
    ctx.events.fire('avatar:fallen', event);
    avatarFallResetCooldownSeconds = AVATAR_FALL_RESET_COOLDOWN_SECONDS;
    ctx.resetNavigation();
    reset();
  }

  return { detect, reset };
}
